import { Link } from "react-router-dom";

import type { Locator, SmeCardView } from "../../../api/types";
import { Card } from "../../../ui/Card";
import { ConfidenceWord } from "../../../ui/ConfidenceWord";
import { NeededByLine } from "../../../ui/NeededByLine";
import { SafeMarkdown } from "../../../ui/SafeMarkdown";
import { SpeechMark } from "../../../ui/SpeechMark";

/** Where on the document a reading came from, in words. */
export function locatorWords(locator: Locator): string {
  const parts: string[] = [];
  if (locator.sheet) parts.push(`sheet ${locator.sheet}`);
  if (locator.page) parts.push(`page ${locator.page}`);
  if (locator.cell) parts.push(`cell ${locator.cell}`);
  if (locator.range) parts.push(`range ${locator.range}`);
  if (locator.heading_path?.length) parts.push(locator.heading_path.join(" › "));
  if (locator.line_no) parts.push(`line ${locator.line_no}`);
  if (locator.xml_id) parts.push(`XML id ${locator.xml_id}`);
  return parts.join(", ");
}

/** Who started, accepted and validated the element. */
function Who({ card }: { card: SmeCardView }) {
  const { element } = card;
  const sentences: string[] = [];
  if (element.originator) sentences.push(`Started by ${element.originator.display}.`);
  if (element.added_by) sentences.push(`Added by hand by ${element.added_by.display}.`);
  else if (element.accepted_by) sentences.push(`Accepted by ${element.accepted_by.display}.`);
  if (sentences.length === 0) return null;
  return <p className="ra-who">{sentences.join(" ")}</p>;
}

/** The source spot, the "needed by" line (once, for the element), and who proposed and accepted. */
export function SourceBlock({ card }: { card: SmeCardView }) {
  const source = card.source;
  const spot = source?.spot;
  return (
    <Card className="ra-section" aria-labelledby="ra-source-title">
      <h2 id="ra-source-title">Where it comes from</h2>
      {spot ? (
        <blockquote className="ra-quote">
          <p>{spot.text_read}</p>
          <footer className="muted">
            {source?.deliverable ? (
              <>
                From <Link to={`/deliverables/${encodeURIComponent(source.deliverable.id)}`}>{source.deliverable.name}</Link>
              </>
            ) : (
              "From a document"
            )}
            {locatorWords(spot.locator) ? `, ${locatorWords(spot.locator)}` : ""}.
          </footer>
        </blockquote>
      ) : source?.text ? (
        <p>{source.text}</p>
      ) : (
        <p className="muted">No source was recorded for this element.</p>
      )}
      <NeededByLine neededBy={card.needed_by} />
      <Who card={card} />
    </Card>
  );
}

/** The agent's logic and confidence word, set in the serif type, and the engineer's note. */
export function WhyBlock({ card }: { card: SmeCardView }) {
  if (!card.logic && !card.confidence && !card.engineer_note) return null;
  return (
    <Card className="ra-section" aria-labelledby="ra-why-title">
      <h2 id="ra-why-title">Why it is proposed</h2>
      {card.logic && (
        <div className="agent-text" data-agent="true">
          <SafeMarkdown>{card.logic}</SafeMarkdown>
        </div>
      )}
      {card.confidence && (
        <p>
          <span className="muted">Confidence: </span>
          <ConfidenceWord confidence={card.confidence} showReason />
        </p>
      )}
      {card.engineer_note && (
        <p className="ra-note">
          <SpeechMark label="Note from the engineer" /> <span className="muted">The engineer wrote:</span> {card.engineer_note}
        </p>
      )}
    </Card>
  );
}
